import React, { useEffect, useMemo, useRef, useState } from "react";
import LabelCardEditor from "./LabelCardEditor";
import { overflowKeysBySerial } from "./limitString";
import { enrichCardLive } from "./enrichCardLive";
import { paperMeta } from "./labelTemplates";
import {
  suggestAbbreviation,
  parseInfosSuggestion,
} from "./suggestAbbreviation";
import InfosModal from "./infosModal";
import FileIcon from "../../../img/fileIcon";
import { modelIdOf } from "./submitTagsFlow";

const ZOOM_STEPS = [2, 2.5, 3, 3.5, 4];

const FIELD_LABEL = {
  nome: "nome",
  edicao: "edição",
  raridade: "raridade",
  infos: "ano / idioma / nº",
  numeracao: "numeração",
  ano: "ano",
  idioma: "idioma",
  serial: "serial",
  nota_geral: "nota",
  nota_autografo: "autógrafo",
};

const TEXT_FIELD_KEYS = new Set([
  "nome",
  "edition",
  "raridade",
  "ano",
  "idioma",
  "num",
]);

/** Campos de texto da etiqueta que podem ser propagados entre cartas do mesmo modelo. */
const MODEL_TEXT_PATCH = (card) => ({
  nome: card.nome,
  edition: card.edition,
  raridade: card.raridade,
  ano: card.ano,
  idioma: card.idioma,
  num: card.num,
});

const modelDiffers = (serial) => {
  const tag = serial?.gradableModel?.tag;
  const model = serial?.gradableModel;
  if (!tag) return false;
  return (
    tag.name !== model?.name ||
    tag.rarity !== model?.rarity ||
    tag.edition !== model?.edition ||
    tag.year !== model?.year ||
    tag.language !== model?.language ||
    tag.numbering !== model?.numbering
  );
};

const formatFieldErrors = (fields) => {
  const labels = (fields || [])
    .map((f) => FIELD_LABEL[f.key] || f.key)
    .filter(Boolean);
  return [...new Set(labels)].join(", ");
};

const groupKeyFor = (card, serialByNumber) => {
  const serial = serialByNumber?.[card?.serial];
  return String(modelIdOf(serial) || card?.serial || "");
};

const modelTitleFor = (card, serialByNumber) => {
  const serial = serialByNumber?.[card?.serial];
  const model = serial?.gradableModel;
  return model?.name || card?.nome || card?.serial || "Modelo";
};

const rowHasProblem = (row, overflowBySerial) =>
  Boolean(row?.error || overflowBySerial[row?.card?.serial]?.size > 0);

const TagCard = ({
  card,
  paper,
  error,
  index,
  scale,
  fontsReady,
  selection,
  dimmed,
  serial,
  douradoEligible,
  overflowKeys,
  errorFields,
  resolved,
  peerCount,
  invertidaPeerCount,
  showApplyToModel,
  showApplyInvertida,
  tagsData,
  serialByNumber,
  updateCard,
  applySuggestion,
  applyToModelPeers,
  applyInvertidaToModelPeers,
  setInfosModel,
}) => {
  const hasOverflow = overflowKeys?.size > 0;
  const suggestions = [];
  if (hasOverflow) {
    for (const fieldKey of ["nome", "edicao", "infos"]) {
      if (!overflowKeys.has(fieldKey)) continue;
      const result = suggestAbbreviation({
        fieldKey,
        card,
        peers: tagsData,
        serialByNumber,
      });
      if (result.suggestion) {
        suggestions.push({ fieldKey, ...result });
      }
    }
  }

  const fieldErrorLabel = formatFieldErrors(errorFields);
  const canPropagate = showApplyToModel && peerCount > 0;
  const canPropagateInvertida =
    showApplyInvertida && !douradoEligible && invertidaPeerCount > 0;

  let frameClass = "border-[#d59106] bg-[#1a1a1a]";
  if (error || hasOverflow) frameClass = "border-red-500 bg-[#1a1212]";
  else if (resolved) frameClass = "border-emerald-500 bg-[#121a14]";

  return (
    <div
      className={`rounded border p-2 relative text-[#ffedd5] ${frameClass}`}
      style={{ opacity: dimmed ? 0.45 : 1 }}
      data-tag-serial={card.serial}
      data-tag-status={error || hasOverflow ? "error" : resolved ? "resolved" : "ok"}
    >
      <div className="flex items-center gap-2 mb-2 text-sm text-[#ffedd5]">
        {selection ? (
          <input
            type="checkbox"
            checked={card.select === true}
            onChange={(event) => updateCard(card.serial, { select: event.target.checked })}
            aria-label={`Selecionar ${card.serial}`}
          />
        ) : null}
        <span className="text-[#d59106]">{index + 1}.</span>
        <span className="font-mono text-[#ffedd5] font-semibold tracking-wide">
          {card.serial}
        </span>
        {paper ? (
          <span className="text-xs px-1.5 py-0.5 rounded bg-[#111] border border-[#d59106]/80 text-[#ffedd5]">
            {paperMeta(paper).label}
          </span>
        ) : null}
        {resolved && !hasOverflow && !error ? (
          <span className="text-xs px-1.5 py-0.5 rounded border border-emerald-500/70 bg-emerald-950/40 text-emerald-300">
            Resolvido
          </span>
        ) : null}
        {modelDiffers(serial) ? (
          <button
            type="button"
            title="Ver informações do modelo"
            onClick={() => setInfosModel(serial?.gradableModel)}
          >
            <FileIcon fill="#d59106" />
          </button>
        ) : null}
        {!douradoEligible ? (
          <div className="ml-auto flex flex-col items-end gap-1">
            <label className="flex items-center gap-1 cursor-pointer text-[#ffedd5]">
              <input
                type="checkbox"
                checked={card.invertida === true}
                onChange={(event) =>
                  updateCard(card.serial, { invertida: event.target.checked })
                }
                aria-label={`Invertida ${card.serial}`}
              />
              <span>Invertida</span>
            </label>
            {canPropagateInvertida ? (
              <button
                type="button"
                className="text-xs px-2 py-0.5 rounded border border-emerald-600/70 bg-[#111] text-emerald-200 hover:bg-[#1a2a1a]"
                onClick={() => applyInvertidaToModelPeers(card)}
              >
                Aplicar invertida às outras {invertidaPeerCount} do modelo
              </button>
            ) : null}
          </div>
        ) : (
          <span className="ml-auto text-xs font-medium text-[#d59106]">Dourado</span>
        )}
      </div>

      {error ? (
        <p className="text-sm text-red-400 py-6 px-2 w-[520px]">
          Subgrades incompletas — preencha as quatro notas ou deixe todas em branco para gerar
          esta etiqueta.
        </p>
      ) : (
        <LabelCardEditor
          key={`label-${card.serial}-${fontsReady ? "fonts" : "nofonts"}`}
          card={card}
          scale={scale}
          overflowKeys={overflowKeys}
          readOnly={dimmed}
          onField={(field, value) => updateCard(card.serial, { [field]: value })}
        />
      )}

      {hasOverflow || fieldErrorLabel ? (
        <div className="mt-1 space-y-1">
          <p className="text-xs text-red-400">
            {fieldErrorLabel
              ? `Corrigir: ${fieldErrorLabel}.`
              : "Texto excede a área útil da etiqueta."}{" "}
            Encurte o campo para liberar o ZIP.
          </p>
          {suggestions.map(({ fieldKey, suggestion, count }) => (
            <div key={fieldKey} className="flex flex-wrap gap-1">
              <button
                type="button"
                className="text-left text-xs px-2 py-1 rounded border border-[#d59106] bg-[#111] text-[#ffedd5] hover:bg-[#2a2a2a]"
                onClick={() => applySuggestion(card, fieldKey, suggestion, false)}
              >
                Usar abreviação de {FIELD_LABEL[fieldKey]} ({count}× no lote):{" "}
                <span className="font-mono text-[#d59106]">{suggestion}</span>
              </button>
              {canPropagate ? (
                <button
                  type="button"
                  className="text-left text-xs px-2 py-1 rounded border border-emerald-600/60 bg-[#111] text-emerald-200 hover:bg-[#1a2a1a]"
                  onClick={() => applySuggestion(card, fieldKey, suggestion, true)}
                >
                  Aplicar abreviação às {peerCount + 1} do modelo
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {canPropagate && !error && !hasOverflow ? (
        <button
          type="button"
          className="mt-2 text-xs px-2 py-1 rounded border border-emerald-600/70 bg-[#111] text-emerald-200 hover:bg-[#1a2a1a]"
          onClick={() => applyToModelPeers(card)}
        >
          Aplicar textos desta etiqueta às outras {peerCount} do modelo
        </button>
      ) : null}
    </div>
  );
};

const TagLabelsWorkspace = ({
  tagsData,
  errorData,
  serials,
  selection,
  setTagsData,
  douradoEligibleFor,
  fontsReady = true,
}) => {
  const [scale, setScale] = useState(3);
  const [infosModel, setInfosModel] = useState(null);
  const [resolvedSerials, setResolvedSerials] = useState(() => new Set());
  const [editedSerials, setEditedSerials] = useState(() => new Set());
  const [invertidaTouchedSerials, setInvertidaTouchedSerials] = useState(() => new Set());
  const [lockedUnitKeys, setLockedUnitKeys] = useState(null);
  const knownProblemsRef = useRef(new Set());
  const orderIdentityRef = useRef(null);

  const overflowBySerial = useMemo(() => overflowKeysBySerial(errorData), [errorData]);
  const errorFieldsBySerial = useMemo(() => {
    const map = {};
    for (const card of errorData?.cards || []) {
      map[card.serial] = card.fields || [];
    }
    return map;
  }, [errorData]);

  const serialByNumber = useMemo(
    () => Object.fromEntries((serials || []).map((s) => [s?.serialNumber, s])),
    [serials]
  );

  const markEdited = (serialNumber) => {
    setEditedSerials((prev) => {
      if (prev.has(serialNumber)) return prev;
      const next = new Set(prev);
      next.add(serialNumber);
      return next;
    });
  };

  const markInvertidaTouched = (serialNumber) => {
    setInvertidaTouchedSerials((prev) => {
      if (prev.has(serialNumber)) return prev;
      const next = new Set(prev);
      next.add(serialNumber);
      return next;
    });
  };

  const updateCard = (serialNumber, patch) => {
    const touchesText = Object.keys(patch || {}).some((k) => TEXT_FIELD_KEYS.has(k));
    if (touchesText) markEdited(serialNumber);
    if (Object.prototype.hasOwnProperty.call(patch || {}, "invertida")) {
      markInvertidaTouched(serialNumber);
    }
    setTagsData((data) =>
      (data || []).map((d) => (d?.serial === serialNumber ? { ...d, ...patch } : d))
    );
  };

  const clearEdited = (serialNumber) => {
    setEditedSerials((prev) => {
      if (!prev.has(serialNumber)) return prev;
      const next = new Set(prev);
      next.delete(serialNumber);
      return next;
    });
  };

  const clearInvertidaTouched = (serialNumber) => {
    setInvertidaTouchedSerials((prev) => {
      if (!prev.has(serialNumber)) return prev;
      const next = new Set(prev);
      next.delete(serialNumber);
      return next;
    });
  };

  const applySuggestion = (card, fieldKey, suggestion, toModelPeers = false) => {
    let patch = null;
    if (fieldKey === "nome") patch = { nome: suggestion };
    else if (fieldKey === "edicao") patch = { edition: suggestion };
    else if (fieldKey === "infos") {
      const parts = parseInfosSuggestion(suggestion);
      if (parts) patch = parts;
    }
    if (!patch) return;

    if (!toModelPeers) {
      markEdited(card.serial);
      updateCard(card.serial, patch);
      return;
    }

    const groupKey = groupKeyFor(card, serialByNumber);
    setTagsData((data) =>
      (data || []).map((d) => {
        if (!d) return d;
        if (groupKeyFor(d, serialByNumber) !== groupKey) return d;
        return { ...d, ...patch };
      })
    );
    clearEdited(card.serial);
  };

  const applyToModelPeers = (sourceCard) => {
    const groupKey = groupKeyFor(sourceCard, serialByNumber);
    const patch = MODEL_TEXT_PATCH(sourceCard);
    setTagsData((data) =>
      (data || []).map((d) => {
        if (!d || d.serial === sourceCard.serial) return d;
        if (groupKeyFor(d, serialByNumber) !== groupKey) return d;
        return { ...d, ...patch };
      })
    );
    clearEdited(sourceCard.serial);
  };

  const applyInvertidaToModelPeers = (sourceCard) => {
    const groupKey = groupKeyFor(sourceCard, serialByNumber);
    const value = sourceCard.invertida === true;
    setTagsData((data) =>
      (data || []).map((d) => {
        if (!d || d.serial === sourceCard.serial) return d;
        if (groupKeyFor(d, serialByNumber) !== groupKey) return d;
        const peerSerial = serialByNumber[d.serial];
        if (douradoEligibleFor(peerSerial, d)) return d;
        return { ...d, invertida: value };
      })
    );
    clearInvertidaTouched(sourceCard.serial);
  };

  const countInvertidaPeers = (sourceCard) => {
    const groupKey = groupKeyFor(sourceCard, serialByNumber);
    let n = 0;
    for (const d of tagsData || []) {
      if (!d || d.serial === sourceCard.serial) continue;
      if (groupKeyFor(d, serialByNumber) !== groupKey) continue;
      const peerSerial = serialByNumber[d.serial];
      if (douradoEligibleFor(peerSerial, d)) continue;
      n += 1;
    }
    return n;
  };

  const rows = useMemo(() => (tagsData || []).map(enrichCardLive), [tagsData]);
  const selectedCount = (tagsData || []).filter((d) => d?.select).length;
  const tagsIdentity = useMemo(
    () => (tagsData || []).map((d) => d?.serial).join("|"),
    [tagsData]
  );

  // Marca “resolvido” quando o problema some — sem reordenar.
  useEffect(() => {
    const problemSerials = new Set();
    for (const row of rows) {
      const serial = row.card?.serial;
      if (!serial) continue;
      if (rowHasProblem(row, overflowBySerial)) problemSerials.add(serial);
    }
    for (const serial of problemSerials) {
      knownProblemsRef.current.add(serial);
    }

    setResolvedSerials((prev) => {
      const next = new Set(prev);
      for (const serial of knownProblemsRef.current) {
        if (!problemSerials.has(serial)) next.add(serial);
        else next.delete(serial);
      }
      if (next.size === prev.size && [...next].every((s) => prev.has(s))) return prev;
      return next;
    });
  }, [rows, overflowBySerial]);

  // Ordem travada após a 1ª validação: modelos com erro primeiro; ao corrigir não remexe.
  useEffect(() => {
    if (!tagsIdentity) {
      orderIdentityRef.current = null;
      setLockedUnitKeys(null);
      setEditedSerials(new Set());
      setInvertidaTouchedSerials(new Set());
      return undefined;
    }

    if (orderIdentityRef.current !== tagsIdentity) {
      orderIdentityRef.current = tagsIdentity;
      setLockedUnitKeys(null);
      setEditedSerials(new Set());
      setInvertidaTouchedSerials(new Set());
    }

    const timer = window.setTimeout(() => {
      setLockedUnitKeys((prev) => {
        if (orderIdentityRef.current !== tagsIdentity) return prev;
        if (prev) return prev;

        const byModel = new Map();
        const modelOrder = [];
        rows.forEach((row, index) => {
          const key = groupKeyFor(row.card, serialByNumber) || `idx-${index}`;
          if (!byModel.has(key)) {
            byModel.set(key, { key, items: [] });
            modelOrder.push(key);
          }
          byModel.get(key).items.push({ ...row, index });
        });

        const units = modelOrder.map((key) => {
          const g = byModel.get(key);
          const hasProblem = g.items.some((row) => rowHasProblem(row, overflowBySerial));
          return {
            unitKey:
              g.items.length >= 2
                ? `cluster:${key}`
                : `single:${g.items[0].card.serial}`,
            hasProblem,
          };
        });
        units.sort((a, b) => Number(b.hasProblem) - Number(a.hasProblem));
        return units.map((u) => u.unitKey);
      });
    }, 280);

    return () => window.clearTimeout(timer);
  }, [tagsIdentity, rows, overflowBySerial, serialByNumber]);

  const needsFixCount = useMemo(() => {
    let n = 0;
    for (const row of rows) {
      if (rowHasProblem(row, overflowBySerial)) n += 1;
    }
    return n;
  }, [rows, overflowBySerial]);

  const displayUnits = useMemo(() => {
    const byModel = new Map();
    const modelOrder = [];
    rows.forEach((row, index) => {
      const key = groupKeyFor(row.card, serialByNumber) || `idx-${index}`;
      if (!byModel.has(key)) {
        byModel.set(key, {
          key,
          title: modelTitleFor(row.card, serialByNumber),
          items: [],
        });
        modelOrder.push(key);
      }
      byModel.get(key).items.push({ ...row, index });
    });

    const units = modelOrder.map((key) => {
      const g = byModel.get(key);
      if (g.items.length >= 2) {
        return {
          unitKey: `cluster:${key}`,
          kind: "cluster",
          title: g.title,
          items: g.items,
          peerCount: g.items.length - 1,
        };
      }
      return {
        unitKey: `single:${g.items[0].card.serial}`,
        kind: "single",
        items: g.items,
        peerCount: 0,
      };
    });

    if (lockedUnitKeys?.length) {
      const rank = new Map(lockedUnitKeys.map((k, i) => [k, i]));
      units.sort((a, b) => {
        const ra = rank.has(a.unitKey) ? rank.get(a.unitKey) : 9999;
        const rb = rank.has(b.unitKey) ? rank.get(b.unitKey) : 9999;
        return ra - rb;
      });
    } else {
      units.sort((a, b) => {
        const pa = a.items.some((row) => rowHasProblem(row, overflowBySerial));
        const pb = b.items.some((row) => rowHasProblem(row, overflowBySerial));
        return Number(pb) - Number(pa);
      });
    }

    return units;
  }, [rows, serialByNumber, overflowBySerial, lockedUnitKeys]);

  const paperSummary = useMemo(() => {
    const counts = {};
    for (const row of rows) {
      if (!row.paper || !row.card?.select) continue;
      counts[row.paper] = (counts[row.paper] || 0) + 1;
    }
    return Object.entries(counts);
  }, [rows]);

  if (!tagsData?.length) {
    return <p className="text-center text-sm text-gray-600 py-8">Nenhuma etiqueta para editar.</p>;
  }

  const renderCard = ({ card, paper, error, index, peerCount }) => {
    const serial = serialByNumber[card.serial];
    const douradoEligible = douradoEligibleFor(serial, card);
    const overflowKeys = overflowBySerial[card.serial];
    const dimmed = selection && !card.select;
    const resolved = resolvedSerials.has(card.serial);
    const showApplyToModel = editedSerials.has(card.serial);
    const showApplyInvertida = invertidaTouchedSerials.has(card.serial);
    const invertidaPeerCount = countInvertidaPeers(card);
    return (
      <TagCard
        card={card}
        paper={paper}
        error={error}
        index={index}
        scale={scale}
        fontsReady={fontsReady}
        selection={selection}
        dimmed={dimmed}
        serial={serial}
        douradoEligible={douradoEligible}
        overflowKeys={overflowKeys}
        errorFields={errorFieldsBySerial[card.serial]}
        resolved={resolved}
        peerCount={peerCount}
        invertidaPeerCount={invertidaPeerCount}
        showApplyToModel={showApplyToModel}
        showApplyInvertida={showApplyInvertida}
        tagsData={tagsData}
        serialByNumber={serialByNumber}
        updateCard={updateCard}
        applySuggestion={applySuggestion}
        applyToModelPeers={applyToModelPeers}
        applyInvertidaToModelPeers={applyInvertidaToModelPeers}
        setInfosModel={setInfosModel}
      />
    );
  };

  return (
    <div className="mt-2 text-[#ffedd5]">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-sm text-[#ffedd5]">
          {selectedCount} de {tagsData.length} selecionadas
        </span>
        {paperSummary.map(([paper, count]) => (
          <span
            key={paper}
            className="text-xs px-2 py-1 rounded border border-[#d59106] bg-[#1a1a1a] text-[#ffedd5] font-medium"
          >
            {paperMeta(paper).label}: {count}
          </span>
        ))}
        <label className="flex items-center gap-2 text-sm ml-auto text-[#ffedd5]">
          Zoom
          <select
            className="border border-[#d59106] rounded px-2 py-1 bg-[#1a1a1a] text-[#ffedd5]"
            value={scale}
            onChange={(event) => setScale(Number(event.target.value))}
          >
            {ZOOM_STEPS.map((step) => (
              <option key={step} value={step}>
                {step}x
              </option>
            ))}
          </select>
        </label>
      </div>

      {needsFixCount > 0 ? (
        <div className="mb-3 rounded border border-red-500/60 bg-[#1a1212] px-3 py-2">
          <p className="text-sm font-semibold text-red-400">
            Precisa corrigir ({needsFixCount})
          </p>
          <p className="text-xs text-red-300/80 mt-0.5">
            Campos fora da área útil ou se sobrepondo — ajuste no lugar (borda vermelha). Modelos
            com erro ficam no início; ao corrigir, a etiqueta só marca como resolvida.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-4 items-start">
        {displayUnits.map((unit) => {
          if (unit.kind === "cluster") {
            return (
              <div
                key={unit.unitKey}
                className="rounded border border-[#d59106]/35 bg-[#141414] p-2 flex flex-col gap-2"
              >
                <span className="text-xs text-[#d59106]">
                  {unit.title}
                  <span className="text-[#ffedd5]/60">
                    {" "}
                    · {unit.items.length} do mesmo modelo
                  </span>
                </span>
                <div className="flex flex-wrap gap-4">
                  {unit.items.map((row) => (
                    <React.Fragment key={row.card?.serial || row.index}>
                      {renderCard({ ...row, peerCount: unit.peerCount })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          }
          const row = unit.items[0];
          return (
            <React.Fragment key={unit.unitKey}>
              {renderCard({ ...row, peerCount: 0 })}
            </React.Fragment>
          );
        })}
      </div>

      {infosModel ? (
        <InfosModal close={() => setInfosModel(null)} model={infosModel} />
      ) : null}
    </div>
  );
};

export default TagLabelsWorkspace;
